import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';

export default class CommonCarousel {
  constructor(element, options = {}) {
    this.container = element;
    if (!this.container) return;

    this.options = {
      carouselSelector: '.carousel',
      itemSelector: '.carousel_item',
      nextBtnSelector: '.c_next_bt',
      prevBtnSelector: '.c_prev_bt',
      ...options,
    };

    this.carousel = this.container.querySelector(this.options.carouselSelector);
    if (!this.carousel) return;

    this.items = this.carousel.querySelectorAll(this.options.itemSelector);
    if (this.items.length <= 1) {
      const nextBtn = this.container.querySelector(this.options.nextBtnSelector);
      const prevBtn = this.container.querySelector(this.options.prevBtnSelector);
      if (nextBtn) nextBtn.style.display = 'none';
      if (prevBtn) prevBtn.style.display = 'none';
      return;
    }

    gsap.registerPlugin(Draggable);
    this.init();
  }

  init() {
    this.setVars();
    this.setDom();
    this.setupCarousel();
    this.initEvents();
  }

  destruct() {
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
    if (this.draggable) {
      this.draggable[0].kill();
    }
    gsap.killTweensOf(this.carousel);
  }

  setVars() {
    this.carouselWrap = null;
    this.nextBtn = null;
    this.prevBtn = null;
    this.itemWidth = 0;
    this.totalItems = 0;
    this.currentIndex = 0;
    this.isAnimating = false;
    this.handleNext = null;
    this.handlePrev = null;
    this.draggable = null;
  }

  setDom() {
    this.carouselWrap = this.container;
    this.nextBtn = this.container.querySelector(this.options.nextBtnSelector);
    this.prevBtn = this.container.querySelector(this.options.prevBtnSelector);
  }

  initEvents() {
    if (this.nextBtn) {
      this.handleNext = () => {
        if (this.isAnimating) return;
        this.goToSlide(this.currentIndex + 1);
      };
      this.nextBtn.addEventListener('click', this.handleNext);
    }

    if (this.prevBtn) {
      this.handlePrev = () => {
        if (this.isAnimating) return;
        this.goToSlide(this.currentIndex - 1);
      };
      this.prevBtn.addEventListener('click', this.handlePrev);
    }
  }

  setupCarousel() {
    this.carousel.style.cursor = 'grab';
    this.totalItems = this.items.length;
    this.currentIndex = 0;

    this.executeResize();
    this.updateButtons();
    this.initDraggable();
  }

  goToSlide(index) {
    if (this.isAnimating) return;

    const newIndex = Math.max(0, Math.min(index, this.totalItems - 1));
    if (newIndex === this.currentIndex && !this.isAnimating) {
      this.updatePosition();
      return;
    }

    this.isAnimating = true;
    this.currentIndex = newIndex;
    this.updatePosition();
    this.updateButtons();
  }

  updatePosition(animate = true) {
    const containerWidth = this.carouselWrap.offsetWidth;
    const itemMargin = parseFloat(getComputedStyle(this.items[0]).marginRight) || 0;
    const itemOffsetWidth = this.items[0].offsetWidth;
    this.itemWidth = itemOffsetWidth + itemMargin;

    // SP表示時にmargin-leftが指定されているため、その分を座標から引く
    const carouselMarginLeft = parseFloat(getComputedStyle(this.carousel).marginLeft) || 0;

    const newX = (containerWidth - itemOffsetWidth) / 2 - this.currentIndex * this.itemWidth - carouselMarginLeft;

    if (animate) {
      gsap.to(this.carousel, {
        x: newX,
        duration: 0.7,
        ease: 'quart.inOut',
        onComplete: () => {
          this.isAnimating = false;
        },
      });
    } else {
      gsap.set(this.carousel, { x: newX });
    }
  }

  updateButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.classList.toggle('disabled', this.currentIndex === 0);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalItems - 1;
      this.nextBtn.classList.toggle('disabled', this.currentIndex === this.totalItems - 1);
    }
  }

  executeResize() {
    if (this.items && this.items.length > 0) {
      this.updatePosition(false);
      if (this.draggable) {
        this.draggable[0].kill();
        this.initDraggable();
      }
    }
  }

  initDraggable() {
    const containerWidth = this.carouselWrap.offsetWidth;
    const itemOffsetWidth = this.items[0].offsetWidth;
    const itemMargin = parseFloat(getComputedStyle(this.items[0]).marginRight) || 0;
    this.itemWidth = itemOffsetWidth + itemMargin;

    // SP表示時にmargin-leftが指定されているため、その分を座標計算に含める
    const carouselMarginLeft = parseFloat(getComputedStyle(this.carousel).marginLeft) || 0;

    const centerOffset = (containerWidth - itemOffsetWidth) / 2;
    const minX = centerOffset - (this.totalItems - 1) * this.itemWidth - carouselMarginLeft;
    const maxX = centerOffset - carouselMarginLeft;

    this.draggable = Draggable.create(this.carousel, {
      type: 'x',
      edgeResistance: 0.8,
      bounds: { minX, maxX },
      onDragStart: () => {
        this.isAnimating = false;
        this.carousel.style.cursor = 'grabbing';
        gsap.killTweensOf(this.carousel);
      },
      onDragEnd: () => {
        this.carousel.style.cursor = 'grab';
        if (this.isAnimating) return;

        const velocityX = this.draggable[0].getDirection('x') === 'left' ? -this.draggable[0].velocityX : this.draggable[0].velocityX;
        const dragDistance = this.draggable[0].x - this.draggable[0].startX;

        // スワイプの速さと距離のしきい値を設定（数値を小さくすると、より敏感に反応します）
        const velocityThreshold = 200; // pixels per second
        const distanceThreshold = this.itemWidth / 8;

        if (Math.abs(velocityX) > velocityThreshold || Math.abs(dragDistance) > distanceThreshold) {
          // しきい値を超えた場合、次のスライドへ移動
          this.goToSlide(this.currentIndex + (dragDistance < 0 ? 1 : -1));
        } else {
          // しきい値に満たない場合、元のスライドにスナップバック
          this.updatePosition();
        }
      },
    });
  }
}